import { useCallback } from 'react'
import { MapContainer, TileLayer } from 'react-leaflet'
import { getZoomLevel, getInitialZoom } from '../utils/mapUtils'
import debugWrapper, { ComponentSelectorButton } from '../utils/debugWrapper'
import { initializeLeafletIcons } from './map/utils'
import { 
  getMapContainerStyle, 
  getMapWrapperClasses, 
  getMapInnerClasses,
  getSearchControlsClasses,
  getMapContentClasses,
  generateMarkerKey,
  generateMapContainerKey,
  shouldShowSearchControls,
  getCommonMapContainerProps,
  getTileLayerUrl
} from '../utils/mapComponentUtils'
import { NormalModeMarker } from './map/MarkerComponents'
import EventMarker from '../components/map/EventMarker'
import ListingMarker from '../components/map/ListingMarker'
import ListingsEmptyNotice from '../components/map/ListingsEmptyNotice'
import ImmersiveMapPanelContent from '../components/map/ImmersiveMapPanelContent'
import MapSearchControls from '../components/map/MapSearchControls'
import MapNotifications from '../components/map/MapNotifications'
import SpotModeUI from '../components/map/SpotModeUI'
import SightingShareCard from '../components/map/SightingShareCard'
import SpotModeMarker from '../components/map/SpotModeMarker'
import MobileMapControls from '../components/map/MobileMapControls'
import FeaturedEntityCard from '../components/map/FeaturedEntityCard'
import { MapZoomTracker } from '../components/map/MapZoomTracker'
import { MyLocationControl } from '../components/map/MyLocationControl'
import DraggablePopupHandler from '../components/DraggablePopupHandler'
import UserLocationMarkerWithRadius from '../components/map/UserLocationMarkerWithRadius'
import MapControllers from '../components/map/MapControllers'
import MapBoundsListener from '../components/map/MapBoundsListener'
import { useHomePageMapState } from '../hooks/map/useHomePageMapState'
import { useHomePageListings } from '../hooks/map/useHomePageListings'
import { useHomePageEvents } from '../hooks/map/useHomePageEvents'
import { useEntityDeepLink } from '../hooks/map/useEntityDeepLink'
import { useHomePageMapEffects } from '../hooks/map/useHomePageMapEffects'
import ProximityNotificationStack from '../components/map/ProximityNotificationStack'
import { useResponsiveLayout } from '../hooks/useResponsiveLayout'

initializeLeafletIcons()

/**
 * HomePageMap - Refactored to use useHomePageMapState + map-layer hooks
 * (listings, events, deep-link, cross-cutting effects extracted to hooks/map/)
 */
function HomePageMap({ 
  isFullscreenMode = false, 
  isImmersiveMode = false, 
  immersiveTabs = null, 
  activeTab = 'search', 
  onTabChange = null, 
  panelState = 'expanded', 
  componentSource = 'Unknown', 
  onUnreadCountChange = null, 
  initialChannelId = null, 
  onChannelSelected = null,
  initialIntent = null,
  claimPrefill = null,
  onClaimPrefillConsumed = null
}) {
  const state = useHomePageMapState({
    isFullscreenMode,
    isImmersiveMode,
    onTabChange,
    panelState,
    activeTab
  })

  const { isDesktop } = useResponsiveLayout()

  const {
    t,
    user,
    logout,
    navigate,
    convertFromKm,
    entities,
    setEntities,
    userLocation,
    setUserLocation,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    isLoading,
    mapRef,
    currentOpenPopupRef,
    locationSource,
    isGeocoding,
    isGettingLocation,
    featuredEntity,
    showingFeaturedEntity,
    setShowingFeaturedEntity,
    featuredEntityReason,
    entitySearchQuery,
    setEntitySearchQuery,
    focusedEntityId,
    setFocusedEntityId,
    isolatedEntityId,
    setIsolatedEntityId,
    filteredEntities,
    activeFilters,
    handleFilterToggle,
    openNowActive,
    toggleOpenNow,
    entitySearchResults,
    isSearching,
    showSearchDropdown,
    setShowSearchDropdown,
    searchSortBy,
    setSearchSortBy,
    isSpotPanelMinimized,
    setIsSpotPanelMinimized,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized,
    isSpotModeActive,
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeSocials,
    setSpotModeSocials,
    spotModeImages,
    setSpotModeImages,
    isSubmittingSpot,
    spotModeStartTime,
    deactivateSpotMode,
    entitySearchInputRef,
    entitySearchInputRefAlt,
    pendingShareSighting,
    setPendingShareSighting,
    pendingAuthSubmit,
    loginFormData,
    loginErrors,
    handleLoginChange,
    handleLoginSubmit,
    registerFormData,
    registerErrors,
    handleRegisterChange,
    handleRegisterSubmit,
    isAuthLoading,
    googleAuth,
    contactFormData,
    contactErrors,
    isContactSubmitting,
    contactSuccess,
    handleContactChange,
    handleContactSubmit,
    stats,
    statsLoading,
    radius,
    mapCenter,
    handleRadiusChange,
    handleBoundsChange,
    geocodeAddress,
    handleSelectEntity,
    handleShare,
    handlePanelStateChange,
    handleFeaturedViewDetails,
    handleFeaturedShowOnMap,
    handleFeaturedJoinChannel,
    handleSpotModeActivation,
    handleSpotSubmit,
    handleCurrentLocation,
    pendingConfirmations,
    loadingConfirmations,
    confirmingId,
    handleConfirmSighting,
    fetchNearbyEntities,
    reverseGeocode,
    saveLocation
  } = state

  // Active events + participant entity IDs for calendar badges
  const { showEvents, setShowEvents, activeEvents, eventParticipantIds } = useHomePageEvents()

  // Open SpotMode pre-populated with a specific entity (triggered from map popup).
  // Pass the entity's last known location as prefilledLocation so the pin lands
  // at the entity's position — not the user's GPS — since the user saw it there.
  const handleSpotEntityFromPopup = useCallback((entity) => {
    const lat = entity.currentLatitude ?? entity.current_latitude
    const lng = entity.currentLongitude ?? entity.current_longitude
    // initialLocation uses { lat, lng } (Leaflet convention) throughout useSpotModeEffects
    const prefilledLocation = lat && lng ? { lat, lng } : null
    handleSpotModeActivation(entity, prefilledLocation)
  }, [handleSpotModeActivation])

  const {
    showListings,
    setShowListings,
    listings,
    refreshListings,
    listingsEmpty,
    listingsEmptyDismissed,
    setListingsEmptyDismissed,
    handlePostListingClick
  } = useHomePageListings({ mapRef, userLocation, mapCenter, user })

  useHomePageMapEffects({
    isImmersiveMode,
    panelState,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized,
    mapRef,
    initialIntent,
    handleCurrentLocation
  })

  // Open entity popup from ?entity=<uuid> deeplink (e.g. city landing page)
  useEntityDeepLink({
    entities,
    mapRef,
    markerRefs: state.markerRefs,
    currentOpenPopupRef: state.currentOpenPopupRef,
    panelState,
    handlePanelStateChange
  })


  return (
    <div className={getMapWrapperClasses(isFullscreenMode, isImmersiveMode)}>
      <div className={getMapInnerClasses(isFullscreenMode, isImmersiveMode)}>
        {/* Unified Search Controls */}
        {shouldShowSearchControls(isSpotModeActive, isFullscreenMode, isImmersiveMode, false) && (
          <div className={getSearchControlsClasses(isImmersiveMode)}>
            {isImmersiveMode ? (
              <>
                <ImmersiveMapPanelContent
                  panelState={panelState}
                  isSearchPanelMinimized={isSearchPanelMinimized}
                  activeTab={activeTab}
                  onTabChange={onTabChange}
                  onPanelStateChange={handlePanelStateChange}
                  immersiveTabs={immersiveTabs}
                  user={user}
                  onLogout={logout}
                  entities={entities}
                  activeFilters={activeFilters}
                  onFilterToggle={handleFilterToggle}
                  openNowActive={openNowActive}
                  onToggleOpenNow={toggleOpenNow}
                  showEvents={showEvents}
                  onToggleShowEvents={() => setShowEvents(prev => !prev)}
                  featuredEntity={featuredEntity}
                  showingFeaturedEntity={showingFeaturedEntity}
                  featuredEntityReason={featuredEntityReason}
                  onFeaturedViewDetails={handleFeaturedViewDetails}
                  onFeaturedShowOnMap={handleFeaturedShowOnMap}
                  onFeaturedJoinChannel={handleFeaturedJoinChannel}
                  searchLocation={searchLocation}
                  onSearchLocationChange={(value) => {
                    setSearchLocation(value)
                    if (!value.trim()) {
                      setUserLocation(null)
                      setSearchLocationName('')
                      setEntities([])
                    }
                  }}
                  onGeocode={geocodeAddress}
                  onCurrentLocation={handleCurrentLocation}
                  onSpotClick={handleSpotModeActivation}
                  isGeocoding={isGeocoding}
                  locationSource={locationSource}
                  entitySearchQuery={entitySearchQuery}
                  onEntitySearchChange={setEntitySearchQuery}
                  entitySearchInputRef={entitySearchInputRef}
                  isSearching={isSearching}
                  searchResults={entitySearchResults}
                  showDropdown={showSearchDropdown}
                  onSelectEntity={handleSelectEntity}
                  onCloseDropdown={() => setShowSearchDropdown(false)}
                  userLocation={userLocation}
                  searchLocationName={searchLocationName}
                  radius={radius}
                  entityCount={entities.length}
                  isLoading={isLoading}
                  onShare={handleShare}
                  stats={stats}
                  statsLoading={statsLoading}
                  pendingConfirmations={pendingConfirmations}
                  loadingConfirmations={loadingConfirmations}
                  confirmingId={confirmingId}
                  handleConfirmSighting={handleConfirmSighting}
                  loginFormData={loginFormData}
                  loginErrors={loginErrors}
                  handleLoginChange={handleLoginChange}
                  handleLoginSubmit={handleLoginSubmit}
                  registerFormData={registerFormData}
                  registerErrors={registerErrors}
                  handleRegisterChange={handleRegisterChange}
                  handleRegisterSubmit={handleRegisterSubmit}
                  isAuthLoading={isAuthLoading}
                  googleLoginUrl={googleAuth}
                  contactFormData={contactFormData}
                  contactErrors={contactErrors}
                  handleContactChange={handleContactChange}
                  handleContactSubmit={handleContactSubmit}
                  isSubmittingContact={isContactSubmitting}
                  onUnreadCountChange={onUnreadCountChange}
                  initialChannelId={initialChannelId}
                  onChannelSelected={onChannelSelected}
                  claimPrefill={claimPrefill}
                  onClaimPrefillConsumed={onClaimPrefillConsumed}
                  searchSortBy={searchSortBy}
                  onSearchSortByChange={setSearchSortBy}
                />
                {/* Mobile map controls moved to fixed top-right — see below */}
              </>
            ) : (
              <MapSearchControls
                t={t}
                searchLocation={searchLocation}
                setSearchLocation={setSearchLocation}
                geocodeAddress={geocodeAddress}
                handleCurrentLocation={() => handleCurrentLocation(isImmersiveMode && onTabChange ? () => onTabChange('_minimize_') : null)}
                handleSpotModeActivation={handleSpotModeActivation}
                isGeocoding={isGeocoding}
                isGettingLocation={isGettingLocation}
                locationSource={locationSource}
                entities={entities}
                setEntities={setEntities}
                setUserLocation={setUserLocation}
                setSearchLocationName={setSearchLocationName}
                entitySearchQuery={entitySearchQuery}
                setEntitySearchQuery={setEntitySearchQuery}
                entitySearchInputRef={entitySearchInputRefAlt}
                isSearching={isSearching}
                entitySearchResults={entitySearchResults}
                showSearchDropdown={showSearchDropdown}
                setShowSearchDropdown={setShowSearchDropdown}
                handleSelectEntity={handleSelectEntity}
                userLocation={userLocation}
                radius={radius}
              />
            )}
          </div>
        )}

        {/* Map controls — fixed right-side vertical stack in immersive mode (all screen sizes) */}
        {isImmersiveMode && (
          <MobileMapControls
            mapRef={mapRef}
            className={`fixed right-4 z-[900] flex-col gap-2 ${isDesktop ? 'top-1/2 -translate-y-1/2' : 'bottom-[180px]'}`}
            onLocationClick={() => handleCurrentLocation(isImmersiveMode && onTabChange ? () => onTabChange('_minimize_') : null)}
            onSpotClick={handleSpotModeActivation}
            onListingsToggle={() => {
              if (showListings) {
                setShowListings(false)
                if (isSpotModeActive) deactivateSpotMode()
              } else {
                setShowListings(true)
              }
            }}
            isListingsActive={showListings}
            onPostListing={user && showListings ? handlePostListingClick : null}
          />
        )}

        {/* Interactive Map */}
        <div className={getMapContentClasses(isFullscreenMode, isImmersiveMode, isSpotModeActive)}>
          {/* Notifications - Only show in normal mode (immersive mode shows them in the top container) */}
          {!isFullscreenMode && !isImmersiveMode && (
            <MapNotifications
              t={t}
              convertFromKm={convertFromKm}
              userLocation={userLocation}
              searchLocationName={searchLocationName}
              radius={radius}
              isLoading={isLoading}
              entities={entities}
              activeFilters={activeFilters}
              handleFilterToggle={handleFilterToggle}
              isFullscreenMode={isFullscreenMode}
              isImmersiveMode={isImmersiveMode}
            />
          )}

          {/* Featured Entity Card */}
          {featuredEntity && showingFeaturedEntity && !isFullscreenMode && !isImmersiveMode && (
            <FeaturedEntityCard
              entity={featuredEntity}
              reason={featuredEntityReason}
              onViewDetails={handleFeaturedViewDetails}
              onShowOnMap={handleFeaturedShowOnMap}
              onJoinChannel={handleFeaturedJoinChannel}
              onDismiss={() => setShowingFeaturedEntity(false)}
              t={t}
            />
          )}

          {/* Proximity notifications — stacked card deck */}
          {!isFullscreenMode && (
            <ProximityNotificationStack
              userLocation={userLocation}
              locationSource={locationSource}
              entities={entities}
              isImmersiveMode={isImmersiveMode}
              onTabChange={onTabChange}
              navigate={navigate}
            />
          )}

          <MapContainer
            key={generateMapContainerKey('home', isFullscreenMode, isImmersiveMode)}
            center={mapCenter}
            zoom={getInitialZoom()}
            {...getCommonMapContainerProps()}
            style={getMapContainerStyle(isFullscreenMode, isImmersiveMode)}
          >
            <TileLayer
              key="tile-layer"
              url={getTileLayerUrl()}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <MapZoomTracker />
            <MapBoundsListener onBoundsChange={handleBoundsChange} onRadiusChange={handleRadiusChange} disabled={isSpotModeActive} />
            <MyLocationControl 
              onLocationClick={() => handleCurrentLocation(isImmersiveMode && onTabChange ? () => onTabChange('_minimize_') : null)}
              onSpotClick={handleSpotModeActivation}
            />
            <DraggablePopupHandler 
              isImmersiveMode={isImmersiveMode}
              isSearchPanelMinimized={isSearchPanelMinimized}
              setIsSearchPanelMinimized={setIsSearchPanelMinimized}
              onTabChange={onTabChange}
              activeTab={activeTab}
              panelState={panelState}
            />
            <UserLocationMarkerWithRadius
              userLocation={userLocation}
              radius={radius}
              entities={entities}
              showUserLocation={state.showUserLocation}
              isEntityMode={false}
              showRadiusCircle={false}
            />
            {filteredEntities.map((entity) => {
              const uniqueKey = generateMarkerKey(entity.id, entity.currentLatitude, entity.currentLongitude)
              return (
                <NormalModeMarker
                  key={uniqueKey}
                  entity={entity}
                  isSpotModeActive={isSpotModeActive}
                  user={user}
                  navigate={navigate}
                  setSpotModeEntity={setSpotModeEntity}
                  setEntitySearchQuery={setEntitySearchQuery}
                  setSpotModeLocation={setSpotModeLocation}
                  selectedEntityId={state.selectedEntityId}
                  setSelectedEntityId={state.setSelectedEntityId}
                  onConfirmSuccess={() => handleConfirmSighting(entity.id)}
                  markerRefs={state.markerRefs}
                  allEntities={entities}
                  focusedEntityId={focusedEntityId}
                  setFocusedEntityId={setFocusedEntityId}
                  isolatedEntityId={isolatedEntityId}
                  setIsolatedEntityId={setIsolatedEntityId}
                  isImmersiveMode={isImmersiveMode}
                  onTabChange={onTabChange}
                  t={t}
                  isSearchPanelMinimized={isSearchPanelMinimized}
                  setIsSearchPanelMinimized={setIsSearchPanelMinimized}
                  currentOpenPopupRef={currentOpenPopupRef}
                  panelState={panelState}
                  onPanelStateChange={handlePanelStateChange}
                  isEventParticipant={eventParticipantIds.has(entity.entityId)}
                  onSpotEntity={handleSpotEntityFromPopup}
                />
              )
            })}
            {showEvents && activeEvents.map((ev) => (
              <EventMarker key={ev.id} event={ev} />
            ))}
            {showListings && listings.map((listing) => (
              <ListingMarker
                key={listing.id}
                listing={listing}
                onFulfilled={() => refreshListings()}
                onDeleted={() => refreshListings()}
                currentOpenPopupRef={currentOpenPopupRef}
              />
            ))}
            {isSpotModeActive && spotModeLocation && (
              <SpotModeMarker
                location={spotModeLocation}
                onLocationChange={setSpotModeLocation}
                userLocation={userLocation}
              />
            )}
            <MapControllers
              mapRef={mapRef}
              isImmersiveMode={isImmersiveMode}
              onTabChange={onTabChange}
              isSearchPanelMinimized={isSearchPanelMinimized}
              setIsSearchPanelMinimized={setIsSearchPanelMinimized}
              isSpotModeActive={isSpotModeActive}
              onLocationSelect={setSpotModeLocation}
              setFocusedEntityId={setFocusedEntityId}
              setIsolatedEntityId={setIsolatedEntityId}
              userLocation={userLocation}
              spotModeLocation={spotModeLocation}
              setSpotModeLocationSetTime={null}
              markers={filteredEntities}
              isEntityMode={false}
              radius={radius}
              panelState={panelState}
              enableDragToLoad={isImmersiveMode}
              fetchNearbyEntities={fetchNearbyEntities}
              reverseGeocode={reverseGeocode}
              setUserLocation={setUserLocation}
              setSearchLocationName={setSearchLocationName}
              setSearchLocation={setSearchLocation}
              saveLocation={saveLocation}
              t={t}
            />
          </MapContainer>

          {/* Listings empty-state notification */}
          {listingsEmpty && !listingsEmptyDismissed && (
            <ListingsEmptyNotice
              t={t}
              user={user}
              onDismiss={() => setListingsEmptyDismissed(true)}
              onPostClick={handlePostListingClick}
            />
          )}

          {/* Spot mode dim overlay — sits on top of map, behind UI controls */}
          {isSpotModeActive && (
            <div className="absolute inset-0 bg-black/15 pointer-events-none z-[600]" />
          )}
        </div>

        {/* Spot Mode UI */}
        {isSpotModeActive && (
          <SpotModeUI
            isActive={isSpotModeActive}
            location={spotModeLocation}
            onLocationSet={setSpotModeLocation}
            onCancel={deactivateSpotMode}
            onSubmit={handleSpotSubmit}
            entity={spotModeEntity}
            onEntityChange={setSpotModeEntity}
            entityType={spotModeEntityType}
            onEntityTypeChange={setSpotModeEntityType}
            entities={entities}
            notes={spotModeNotes}
            onNotesChange={setSpotModeNotes}
            entityStatus={spotModeEntityStatus}
            onEntityStatusChange={setSpotModeEntityStatus}
            socials={spotModeSocials}
            onSocialsChange={setSpotModeSocials}
            images={spotModeImages}
            onImagesChange={setSpotModeImages}
            isSubmitting={isSubmittingSpot}
            isPanelMinimized={isSpotPanelMinimized}
            onPanelMinimizeToggle={setIsSpotPanelMinimized}
            user={user}
            isAuthenticated={state.isAuthenticated}
            pendingAuthSubmit={pendingAuthSubmit}
            isImmersiveMode={isImmersiveMode}
            isLocatingGPS={isGettingLocation && !spotModeLocation}
            onListingCreated={(listing) => { refreshListings() }}
          />
        )}

        {/* Post-submit share card — floats above tab bar */}
        {pendingShareSighting && (
          <SightingShareCard
            sightingId={pendingShareSighting.sightingId}
            entityName={pendingShareSighting.entityName}
            onDismiss={() => setPendingShareSighting(null)}
            t={t}
          />
        )}
      </div>
      
      {/* Component Selector - Only visible when admin has enabled debug mode */}
      <ComponentSelectorButton floating={true} />
    </div>
  )
}

export default debugWrapper(HomePageMap, 'HomePageMap', 'src/pages/HomePageMap.jsx')
